import { useState, useMemo, useCallback } from 'react'
import { useLocation } from 'wouter'
import useSWR from 'swr'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { ChartTooltip } from '@/components/ui/chart'
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts'
import { ChevronLeft, CircleDollarSign, Coins, HandCoins, ListFilter, Loader2 } from 'lucide-react'
import { Transaction, TRANSACTION_TYPES, TransactionType } from '@/constants'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'

export default function TransactionsPage() {
	const [, navigate] = useLocation()
	const [dateRange, setDateRange] = useState('all')
	const [transactionType, setTransactionType] = useState<
		TransactionType | 'all'
	>('all')
	const [searchQuery, setSearchQuery] = useState('')
	const { walletAddress } = useUser()

	const fetchUserWalletTransactions = useCallback(
		(url: string) =>
			apiFetch(url, {
				withCredentials: true
			}).then((res) => res.json()),
		[]
	)
	const { data: transactions, isLoading } = useSWR<Transaction[]>(
		walletAddress ? `/v1/transactions/${walletAddress}` : null,
		(url: string) => fetchUserWalletTransactions(url),
		{ revalidateOnMount: true, revalidateOnFocus: true }
	)

	// Filter transactions based on current filters
	const filteredTransactions = useMemo(() => {
		if (!transactions) return []

		return transactions.filter((tx) => {
			const matchesType =
				transactionType === 'all' || transactionType === tx.type

			const matchesSearch =
				searchQuery === '' ||
				tx.toAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
				tx.fromAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
				tx.orderId?.toLowerCase().includes(searchQuery.toLowerCase())

			const txDate = new Date(tx.timestamp)
			const now = new Date()
			let matchesDate = true

			switch (dateRange) {
				case 'today':
					matchesDate = txDate.toDateString() === now.toDateString()
					break
				case 'week':
					const weekAgo = new Date()
					weekAgo.setDate(weekAgo.getDate() - 7)
					matchesDate = txDate >= weekAgo
					break
				case 'month':
					const monthAgo = new Date()
					monthAgo.setMonth(monthAgo.getMonth() - 1)
					matchesDate = txDate >= monthAgo
					break
			}

			return matchesType && matchesSearch && matchesDate
		}).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
	}, [transactions, dateRange, transactionType, searchQuery])

	// Calculate statistics
	const stats = useMemo(() => {
		if (!filteredTransactions.length) return { total: 0, count: 0, average: 0 }

		const total = filteredTransactions.reduce(
			(sum, tx) => sum + parseFloat(tx.amount.toString()),
			0
		)

		return {
			total: total.toFixed(6),
			count: filteredTransactions.length,
			average: (total / filteredTransactions.length).toFixed(6)
		}
	}, [filteredTransactions])

	// Prepare chart data
	const chartData = useMemo(() => {
		if (!filteredTransactions.length) return []

		const groupedByDate = filteredTransactions.reduce((acc, tx) => {
			const date = new Date(tx.timestamp).toLocaleDateString()
			acc[date] = (acc[date] || 0) + parseFloat(tx.amount.toString())
			return acc
		}, {} as Record<string, number>)

		return Object.entries(groupedByDate).map(([date, amount]) => ({
			date,
			amount: (amount as number).toFixed(6)
		}))
	}, [filteredTransactions])

	const getTransactionTypeOptions = () =>
		Object.entries(TRANSACTION_TYPES) as [TransactionType | 'all', string][]

	return (
		<div className="min-h-screen bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				{/* Header with back button */}
				<div
					role="banner"
					className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="absolute left-0
							h-8 w-8
							flex items-center justify-center
							rounded-full
							bg-white/10 hover:bg-white/20
							focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]' />
					</button>

					<h1 className="text-base font-semibold tracking-wide">
						Transactions
					</h1>
				</div>

				{/* Page Header */}
				<div className="text-start mb-3">
					<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">Transaction Reports</h1>
					<p className="text-[13px] leading-[15px] font-regular text-[#BABABB]">
						View and analyze your transaction history
					</p>
				</div>

				<div className="w-full rounded-[8px] px-3 py-4
            bg-gradient-to-r from-[#1D0C50] via-[#1E1534] to-[#141026]
            flex items-center justify-between
            shadow-lg relative overflow-hidden mb-2">

				<div className='bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute top-[-256px] right-[74px] h-[304px] w-[18px] blur-[20px]'></div>
				<div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[20px] right-[36px] h-[304px] w-[18px] blur-[10px]"></div>
			    <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[24px] right-[-14px] h-[304px] w-[18px] blur-[15px] "></div>

					  <span className="dot top-[18%] left-[12%]"></span>
                      <span className="dot top-[32%] left-[88%]"></span>
                      <span className="dot top-[22%] left-[48%]"></span>
                      <span className="dot top-[40%] left-[22%]"></span>
                      <span className="dot top-[18%] left-[75%]"></span>
                      <span className="dot top-[72%] left-[20%]"></span>
                      <span className="dot top-[60%] left-[38%]"></span>
                      <span className="dot top-[48%] left-[55%]"></span>
                      <span className="dot top-[85%] left-[72%]"></span>

					<div className="flex items-center gap-2">
						<div className="w-10 h-10 rounded-[6px] bg-white/10 flex items-center justify-center">

							<HandCoins className='h-6 w-6 text-[#ffffff]'/>
						</div>

						<span className="text-[#ffffff] text-sm leading-[20px] font-medium font-figtree">
							Total Volume
						</span>
					</div>


					<div className="bg-white/10 backdrop-blur-md
              px-[14px] py-1 rounded-[6px]
              text-[#ffffff] text-[22px] font-figtree font-semibold tracking-wide">
						{stats.total} <span className="text-sm font-semibold font-figtree">SECH</span>
					</div>


					<div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
				</div>

				<div className="w-full rounded-[8px] px-3 py-4
            bg-gradient-to-r from-[#0C3950] via-[#153422] to-[#1D1026]
            flex items-center justify-between
            shadow-lg relative overflow-hidden mb-2">

				<div className='bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute top-[-256px] right-[74px] h-[304px] w-[18px] blur-[20px]'></div>
				<div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[20px] right-[36px] h-[304px] w-[18px] blur-[10px]"></div>
			    <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[24px] right-[-14px] h-[304px] w-[18px] blur-[15px] "></div>

					  <span className="dot top-[18%] left-[12%]"></span>
                      <span className="dot top-[32%] left-[88%]"></span>
                      <span className="dot top-[22%] left-[48%]"></span>
                      <span className="dot top-[40%] left-[22%]"></span>
                      <span className="dot top-[18%] left-[75%]"></span>
                      <span className="dot top-[72%] left-[20%]"></span>
                      <span className="dot top-[60%] left-[38%]"></span>
                      <span className="dot top-[48%] left-[55%]"></span>
                      <span className="dot top-[85%] left-[72%]"></span>

					<div className="flex items-center gap-2">
						<div className="w-10 h-10 rounded-[6px] bg-white/10 flex items-center justify-center">

							<Coins className='h-6 w-6 text-[#ffffff]'/>
						</div>

						<span className="text-[#ffffff] text-sm leading-[20px] font-medium font-figtree">
							Transaction Count
						</span>
					</div>


					<div className="bg-white/10 backdrop-blur-md
              px-[14px] py-1 rounded-[6px]
              text-[#ffffff] text-[22px] font-figtree font-semibold tracking-wide">
						{stats.count}
					</div>


					<div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
				</div>

				<div className="w-full rounded-[8px] mb-6 px-3 py-4
            bg-gradient-to-r from-[#4D0C50] via-[#4E2318] to-[#261F10]
            flex items-center justify-between
            shadow-lg relative overflow-hidden">

				<div className='bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute top-[-256px] right-[74px] h-[304px] w-[18px] blur-[20px]'></div>
				<div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[20px] right-[36px] h-[304px] w-[18px] blur-[10px]"></div>
			    <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[24px] right-[-14px] h-[304px] w-[18px] blur-[15px] "></div>

					  <span className="dot top-[18%] left-[12%]"></span>
                      <span className="dot top-[32%] left-[88%]"></span>
                      <span className="dot top-[22%] left-[48%]"></span>
                      <span className="dot top-[40%] left-[22%]"></span>
                      <span className="dot top-[18%] left-[75%]"></span>
                      <span className="dot top-[72%] left-[20%]"></span>
                      <span className="dot top-[60%] left-[38%]"></span>
                      <span className="dot top-[48%] left-[55%]"></span>
                      <span className="dot top-[85%] left-[72%]"></span>

					<div className="flex items-center gap-2">
						<div className="w-10 h-10 rounded-[6px] bg-white/10 flex items-center justify-center">

						    <CircleDollarSign className='h-6 w-6 text-[#ffffff]'/>
						</div>

						<span className="text-[#ffffff] text-sm leading-[20px] font-medium font-figtree">
							Average Amount
						</span>
					</div>


					<div className="bg-white/10 backdrop-blur-md
              px-[14px] py-1 rounded-[6px]
              text-[#ffffff] text-[22px] font-figtree font-semibold tracking-wide">
						{stats.average} <span className="text-sm font-semibold font-figtree">SECH</span>
					</div>


					<div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
				</div>


				

				<div className="flex flex-col gap-6">
					

					{/* Chart */} 
					<Card className="px-3 py-4 bg-[#2F2F32] border-0 rounded-[16px] ">
						<h2 className="text-sm leading-4 font-medium font-figtree mb-4 text-[#ffffff]">Transaction Volume</h2>
						<div className="h-[300px] w-full">
							<ResponsiveContainer width="100%" height="100%">
								<BarChart
									data={chartData}
									margin={{ top: 20, right: 30, left: 40, bottom: 20 }}>
									<XAxis
										dataKey="date"
										stroke="#BABABB"
										fontSize={12}
										tickLine={false}
									/>
									<YAxis
										stroke="#BABABB"
										fontSize={12}
										tickLine={false}
										tickFormatter={(value) => `${value.toFixed(2)} SECH`}
									/>
									<Bar
										dataKey="amount"
										fill="#EF4444"
										radius={[4, 4, 0, 0]}
									/>
									<ChartTooltip
										cursor={{ fill: 'rgba(255,255,255,0.1)' }}
										content={({ active, payload }) => {
											if (!active || !payload?.length) return null
											return (
												<div className="rounded-lg border border-[#4C4C4C] bg-[#2F2F32] p-2 shadow-sm">
													<div className="grid grid-cols-2 gap-2">
														<div className="flex flex-col">
															<span className="text-[0.70rem] uppercase text-[#BABABB]">
																Date
															</span>
															<span className="font-bold text-[#ffffff]">
																{payload[0].payload.date}
															</span>
														</div>
														<div className="flex flex-col">
															<span className="text-[0.70rem] uppercase text-[#BABABB]">
																Amount
															</span>
															<span className="font-bold text-[#ffffff]">
																{Number(payload[0].value)?.toFixed(6)} SECH
															</span>
														</div>
													</div>
												</div>
											)
										}}
									/>
								</BarChart>
							</ResponsiveContainer>
						</div>
					</Card>

					{/* Filters */}
					<div className="flex flex-col sm:flex-row gap-2">
						<div className="flex items-center justify-between w-full">
							<Input
								placeholder="Search by address or order ID..."
								className="flex-none h-[48px] rounded-[8px] !bg-[rgba(0,0,0,0.1)] border border-[rgba(255,255,255,0.12)] w-[calc(100%-56px)] bg-[#3C3C3C] text-[#ffffff] placeholder:text-[#BABABB]"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
							<button className='h-[48px] w-[48px] flex items-center justify-center bg-[#252525] rounded-[8px] '>
								<ListFilter className='w-[18px] h-[18px] text-[#ffffff]'/>
							</button>
						</div>
						{/* <Select value={dateRange} onValueChange={setDateRange}>
							<SelectTrigger className="flex-1 sm:w-[180px] sm:flex-none bg-[#3C3C3C] border-0 text-[#ffffff]">
								<SelectValue placeholder="Select date range" />
							</SelectTrigger>
							<SelectContent className="bg-[#3C3C3C] border-[#4C4C4C]">
								<SelectItem value="all" className="text-[#ffffff] focus:bg-[#4C4C4C] focus:text-[#ffffff]">All Time</SelectItem>
								<SelectItem value="today" className="text-[#ffffff] focus:bg-[#4C4C4C] focus:text-[#ffffff]">Today</SelectItem>
								<SelectItem value="week" className="text-[#ffffff] focus:bg-[#4C4C4C] focus:text-[#ffffff]">Last 7 Days</SelectItem>
								<SelectItem value="month" className="text-[#ffffff] focus:bg-[#4C4C4C] focus:text-[#ffffff]">Last 30 Days</SelectItem>
							</SelectContent>
						</Select> */}
						{/* <Select
							value={transactionType}
							onValueChange={(value: TransactionType | 'all') =>
								setTransactionType(value)
							}>
							<SelectTrigger className="flex-1 sm:w-[180px] sm:flex-none bg-[#3C3C3C] border-0 text-[#ffffff]">
								<SelectValue placeholder="Select type" />
							</SelectTrigger>
							<SelectContent className="bg-[#3C3C3C] border-[#4C4C4C]">
								{getTransactionTypeOptions().map(([type, label]) => (
									<SelectItem key={type} value={type} className="text-[#ffffff] focus:bg-[#4C4C4C] focus:text-[#ffffff]">
										{label}
									</SelectItem>
								))}
							</SelectContent>
						</Select> */}
					</div>

					{/* Transactions Table */}
					<Card className="bg-[#3C3C3C] mt-[-12px] rounded-[16px] border-0 overflow-x-auto">
						<Table className="min-w-[540px] bg-[#2F2F32]">
							<TableHeader className='bg-[#3E3E43]'>
								<TableRow className="!border-0 hover:bg-transparent">
									<TableHead className="text-[#ffffff] h-9 text-[13px] leading-[18px] font-medium font-figtree">Date & Time</TableHead>
									<TableHead className="text-[#ffffff] h-9 text-[13px] leading-[18px] font-medium font-figtree">Type</TableHead>
									<TableHead className="text-[#ffffff] h-9 text-[13px] leading-[18px] font-medium font-figtree">From</TableHead>
									<TableHead className="text-[#ffffff] h-9 text-[13px] leading-[18px] font-medium font-figtree">To</TableHead>
									<TableHead className="text-[#ffffff] h-9 text-[13px] leading-[18px] font-medium font-figtree">Amount (SECH)</TableHead>
									<TableHead className="text-[#ffffff] h-9 text-[13px] leading-[18px] font-medium font-figtree">Order ID</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									<TableRow key="dataLoading" className="hover:bg-transparent">
										<TableCell colSpan={6} className="text-center h-40">
											<div className="flex flex-row items-center justify-center gap-2">
												<Loader2 className="h-6 w-6 animate-spin text-primary" />
												<p className="text-md text-[#BABABB]">
													Fetching transactions data...
												</p>
											</div>
										</TableCell>
									</TableRow>
								) : filteredTransactions?.length ? (
									filteredTransactions.map((tx) => (
										<TableRow key={tx.id} className="border-b border-[#4C4C4C] hover:bg-[rgba(255,255,255,0.05)]">
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[36px] text-xs leading-4 font-medium font-figtree">
												{new Date(tx.timestamp).toLocaleString()}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[36px] text-xs leading-4 font-medium font-figtree">{TRANSACTION_TYPES[tx.type]}</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[36px] text-xs leading-4 font-medium font-figtree">
												{tx.fromAddress.slice(0, 6)}...
												{tx.fromAddress.slice(-4)}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[36px] text-xs leading-4 font-medium font-figtree">
												{tx.toAddress.slice(0, 6)}...{tx.toAddress.slice(-4)}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[36px] text-xs leading-4 font-medium font-figtree">
												{parseFloat(tx.amount.toString()).toFixed(6)}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[36px] text-xs leading-4 font-medium font-figtree">{tx.orderId || '-'}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow key="dataLoading" className="hover:bg-transparent">
										<TableCell colSpan={6} className="text-center h-40">
											<div className="flex flex-row items-center justify-center gap-2">
												<p className="text-md text-[#BABABB]">
													No transactions found
												</p>
											</div>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</Card>
				</div>
			</div>
		</div>
	)
}